import {HttpEvent, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {inject, Injector} from "@angular/core";
import {catchError, from, Observable, switchMap, throwError} from "rxjs";
import {AuthService, Tokens} from "./services/auth/auth.service";
import {environment} from "../../environments/environment";
import {UserService} from "./services/user.service";
import {SessionExpiredError} from "./errors";

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  const authService = inject(AuthService);
  // Resolved lazily: UserService itself depends on HttpClient.
  const injector = inject(Injector);

  const send = (request: HttpRequest<unknown>, tokens: Tokens | undefined): Observable<HttpEvent<unknown>> =>
    next(tokens ? request.clone({setHeaders: {Authorization: `Bearer ${tokens.accessToken}`}}) : request);

  const logoutAndFail = (): Observable<HttpEvent<unknown>> => {
    // onUnauthorized shows the message and sends the user to login; the error
    // only stops the caller and gets the failure recorded.
    void authService.onUnauthorized();
    return throwError(() => new SessionExpiredError());
  };

  return from(authService.getTokens()).pipe(
    switchMap(tokens => send(req, tokens).pipe(
      catchError(error => {
        // 403 is a permission error — the user is authenticated but not allowed
        // to do this. Don't touch the session; let the caller handle it.
        if (error.status !== 401) {
          return throwError(() => error);
        }

        // 401 without prior tokens: nothing to refresh. If the app still shows
        // a signed-in user, the session died under it (refresh token revoked or
        // expired, so the tokens were cleared) and the user has to log in again.
        // Otherwise the request was anonymous to begin with.
        if (!tokens) {
          const user = injector.get(UserService).user();
          return user && user.status !== 'unauthorized' ? logoutAndFail() : throwError(() => error);
        }

        // 401 with tokens: try one force-refresh + retry before giving up.
        // The token may have expired between getTokens() and the request, or
        // Cognito rotated/revoked it. Only log out if the refresh itself fails
        // or the retried request also returns 401.
        return from(authService.forceRefresh()).pipe(
          switchMap(refreshed => {
            if (!refreshed) {
              return logoutAndFail();
            }
            return send(req, refreshed).pipe(
              catchError(retryError => retryError.status === 401
                ? logoutAndFail()
                : throwError(() => retryError))
            );
          })
        );
      })
    ))
  );
};
